import { createFileRoute } from "@tanstack/react-router";
import { LayoutHeader } from "@/Header";
import { Loading } from "@/Loading";
import { useCompanyDetail } from "~/services/customer/home";
import { useTicketList } from "~/services/customer/myPage";
import { CompanyImageSlider } from "@/customer/company/CompanyImageSlider";
import { CompanyBasicInfoCard } from "@/customer/company/CompanyBasicInfoCard";
import { SubscriptionPlansSection } from "@/customer/company/SubscriptionPlansSection";
import { InfoCard } from "@/customer/company/InfoCard";
import { PlaygroundSection } from "@/customer/company/PlaygroundSection";

interface SearchDetailParams {
  companySeq?: string;
  masterYn?: string;
}

export const Route = createFileRoute("/customer/layout/home-serach-detail")({
  component: SearchDetail,
  validateSearch: (search: Record<string, unknown>): SearchDetailParams => {
    return {
      // URL 직렬화 방식에 따라 숫자/문자열 어느 쪽으로 와도 문자열로 정규화
      companySeq:
        search.companySeq != null ? String(search.companySeq) : undefined,
      masterYn: search.masterYn as string | undefined,
    };
  },
});

function SearchDetail() {
  const { companySeq, masterYn } = Route.useSearch();
  const companySeqNumber = companySeq ? Number.parseInt(companySeq, 10) : 0;

  const {
    data: companyDetail,
    isLoading,
    error,
  } = useCompanyDetail(companySeqNumber, masterYn, !!companySeqNumber);

  // 사용 가능한 이용권 목록 조회
  const { data: ticketList = [], isLoading: isTicketListLoading } =
    useTicketList(true, companySeqNumber || undefined);

  const resolvedMasterYn = (masterYn || companyDetail?.masterYn || "N")
    .toString()
    .trim()
    .toUpperCase();

  if (isLoading) {
    return (
      <>
        <LayoutHeader title="로딩 중..." hasBackBtn hasFootPrint={false} />
        <div className="flex justify-center items-center flex-auto">
          <Loading visible={true} />
        </div>
      </>
    );
  }

  if (error || !companyDetail) {
    return (
      <>
        <LayoutHeader title="에러" hasBackBtn hasFootPrint={false} />
        <div className="flex justify-center items-center flex-auto">
          <div className="text-center text-gray-500">
            <p>업체 정보를 불러올 수 없습니다.</p>
            <p className="mt-1 text-sm">잠시 후 다시 시도해주세요.</p>
          </div>
        </div>
      </>
    );
  }

  // 영업 상태 표시
  const getOperatingStatus = () => {
    if (companyDetail.close) {
      return { text: "영업 종료", color: "bg-gray-400" };
    }
    if (companyDetail.endTime) {
      return {
        text: `영업 중 · ${companyDetail.endTime} 종료`,
        color: "bg-green-500",
      };
    }
    return { text: "영업 중", color: "bg-green-500" };
  };

  const operatingStatus = getOperatingStatus();

  // 이 업체의 이용권을 하나라도 보유하고 있으면 예약하기 버튼 노출
  // (useTicketList는 isUseable=true + companySeq 필터로 호출되어 해당 업체의 사용 가능 이용권만 반환)
  const showReservationButton =
    !isTicketListLoading && (ticketList?.length ?? 0) > 0;

  return (
    <>
      <LayoutHeader
        title={companyDetail.companyName}
        hasBackBtn
        hasFootPrint={false}
      />
      <div className="flex flex-col flex-auto bg-[#F5F5F5] pb-24">
        {/* 이미지 슬라이더 */}
        <CompanyImageSlider fileUrls={companyDetail.fileUrls} media={companyDetail.media} />

        {/* 기본 정보 카드 */}
        <CompanyBasicInfoCard
          companyName={companyDetail.companyName}
          companyAddr={companyDetail.companyAddr}
          companySeq={companySeqNumber}
          masterYn={resolvedMasterYn}
          mainTelNumber={companyDetail.mainTelNumber}
          operatingStatus={operatingStatus}
          businessTimes={companyDetail.businessTimes}
          showReservationButton={showReservationButton}
        />

        {/* 상세 정보 섹션 */}
          {/* 서비스 미운영 상태 알림 */}
        {!companyDetail.active && (
          <div className="px-4 space-y-3 pb-4">
              <div className="p-4 bg-white rounded-xl shadow-sm">
                <div className="flex gap-2 items-center">
                  <svg
                    className="w-5 h-5 text-yellow-600"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                  </svg>
                  <p className="text-sm font-medium text-gray-700">매장에서 서비스를 시작하지 않았습니다.</p>
                </div>
              </div>
          </div>
        )}

        {/* 이용권 섹션 */}
        <SubscriptionPlansSection
          companySeq={companySeqNumber}
          companyName={companyDetail.companyName}
          tickets={companyDetail.tickets}
          ticketList={ticketList}
          isTicketListLoading={isTicketListLoading}
          isServiceActive={companyDetail.active}
        />

        {/* 상세 정보 섹션 */}
        <div className="px-4 space-y-3">

          {/* 제공 서비스 */}
          <InfoCard
            title="제공 서비스"
            items={companyDetail.provides}
            icon={
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            }
          />

          {/* 실외 운동장 */}
          <PlaygroundSection playgroundYn={companyDetail.playgroundYn} />

          {/* 보유 시설 */}
          <InfoCard
            title="보유 시설"
            items={companyDetail.facilities}
            icon={
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            }
          />

          {/* 수용 가능 체급 */}
          <InfoCard
            title="수용 가능 체급"
            items={
              companyDetail.maxWeight
                ? [
                    {
                      productName: `~${companyDetail.maxWeight}kg`,
                      active: true,
                      select: true,
                    },
                  ]
                : null
            }
            icon={
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            }
          />
        </div>
      </div>

      {/* 하단 고정 버튼 (이용권 유무 및 구매 가능한 이용권 유무에 따라 버튼 표시) */}
      {/* 
        예약하기 버튼: 내가 가진 해당 업체의 이용권이 있을 경우 (구매 가능한 이용권 유무와 상관없이)
        이용권 구매 버튼: 해당 업체의 구매 가능한 이용권이 있고, 내가 해당 업체의 이용권이 없을 경우
      */}
      {/* {(() => {
        // 내가 가진 해당 업체의 이용권이 있는 경우
        if (ticketList.length > 0) {
          return (
            <div className="fixed right-0 left-0 bottom-[calc(var(--nav-bottom,0px)+5rem)] z-40 px-4 py-3 bg-white border-t border-gray-200 shadow-lg">
              {isTicketListLoading ? (
                <Button
                  className="py-3 w-full text-base font-semibold text-white bg-gray-400 rounded-lg"
                  disabled
                >
                  로딩 중...
                </Button>
              ) : (
                <Button
                  className="py-3 w-full text-base font-semibold text-white bg-orange-400 rounded-lg transition-colors hover:bg-orange-500 active:bg-orange-600"
                  onClick={() => {
                    history.push(
                      `/customer/layout/reservation-with-subscription?companySeq=${companySeqNumber}`
                    );
                  }}
                >
                  예약하기
                </Button>
              )}
            </div>
          );
        }
        // 내가 가진 이용권이 없고, 구매 가능한 이용권이 있는 경우
        if (
          companyDetail.tickets &&
          companyDetail.tickets.length > 0 &&
          !isTicketListLoading
        ) {
          return (
            <div className="fixed right-0 left-0 bottom-[calc(var(--nav-bottom,0px)+5rem)] z-40 px-4 py-3 bg-white border-t border-gray-200 shadow-lg">
              <Button
                className="py-3 w-full text-base font-semibold text-white bg-orange-400 rounded-lg transition-colors hover:bg-orange-500 active:bg-orange-600"
                onClick={() => {
                  navigate({
                    to: "/customer/layout/subscription-list",
                    search: { companySeq: companySeq },
                  });
                }}
              >
                이용권 구매
              </Button>
            </div>
          );
        }
        // 그 외의 경우 버튼 표시 안 함
        return null;
      })()} */}
    </>
  );
}
